import React, { useState, useEffect, useMemo } from 'react';
import { XIcon, PlusIcon, DropletIcon } from 'lucide-react';
import type {
  BloodParameterDefinition,
  BloodParameterReadingWithDefinition,
  BloodParameterFormData,
} from '../../types/blood-parameters';
import { useBloodParameterDefinitions } from '../../hooks/useBloodParameters';

interface BloodParameterFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: BloodParameterFormData) => void;
  isSubmitting: boolean;
  editingReading?: BloodParameterReadingWithDefinition | null;
}

const initialFormState: BloodParameterFormData = {
  parameter_definition_id: null,
  custom_parameter_name: '',
  value: '',
  unit: '',
  test_date: '',
  notes: '',
};

function toDateInputValue(date?: Date | string): string {
  if (!date) {
    const now = new Date();
    return now.toISOString().slice(0, 10);
  }
  if (typeof date === 'string') {
    return date.slice(0, 10);
  }
  return date.toISOString().slice(0, 10);
}

// Group definitions by category for the dropdown
function groupDefinitionsByCategory(definitions: BloodParameterDefinition[]) {
  const grouped: Record<string, BloodParameterDefinition[]> = {};

  for (const def of definitions) {
    const category = def.category || 'Other';
    if (!grouped[category]) {
      grouped[category] = [];
    }
    grouped[category].push(def);
  }

  // Sort categories alphabetically, but put "Other" and "Custom" at the end
  const sortedCategories = Object.keys(grouped).sort((a, b) => {
    if (a === 'Other' || a === 'Custom') return 1;
    if (b === 'Other' || b === 'Custom') return -1;
    return a.localeCompare(b);
  });

  return sortedCategories.map((category) => ({
    category,
    definitions: grouped[category].sort((a, b) =>
      a.display_name.localeCompare(b.display_name),
    ),
  }));
}

export function BloodParameterFormModal({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting,
  editingReading,
}: BloodParameterFormModalProps) {
  const [formState, setFormState] = useState<BloodParameterFormData>(initialFormState);
  const [formError, setFormError] = useState<string | null>(null);
  const [showCustomInput, setShowCustomInput] = useState(false);

  // Fetch parameter definitions
  const { data: definitions = [], isLoading: definitionsLoading } =
    useBloodParameterDefinitions();

  // Group definitions by category
  const groupedDefinitions = useMemo(
    () => groupDefinitionsByCategory(definitions),
    [definitions],
  );

  // Find selected definition (if any)
  const selectedDefinition = useMemo(() => {
    if (!formState.parameter_definition_id) return null;
    return definitions.find((d) => d.id === formState.parameter_definition_id);
  }, [formState.parameter_definition_id, definitions]);

  // Initialize form when editing
  useEffect(() => {
    if (!isOpen) return;

    if (editingReading) {
      setFormState({
        parameter_definition_id: editingReading.parameter_definition_id,
        custom_parameter_name: editingReading.custom_parameter_name || '',
        value: String(editingReading.value),
        unit: editingReading.unit,
        test_date: toDateInputValue(editingReading.test_date),
        notes: editingReading.notes || '',
      });
      setShowCustomInput(!!editingReading.custom_parameter_name);
    } else {
      setFormState({
        ...initialFormState,
        test_date: toDateInputValue(),
      });
      setShowCustomInput(false);
    }
    setFormError(null);
  }, [editingReading, isOpen]);

  // When parameter selection changes, auto-fill unit
  useEffect(() => {
    if (selectedDefinition && !editingReading) {
      setFormState((prev) => ({
        ...prev,
        unit: selectedDefinition.default_unit,
      }));
    }
  }, [selectedDefinition, editingReading]);

  const handleParameterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;

    if (value === 'custom') {
      setShowCustomInput(true);
      setFormState((prev) => ({
        ...prev,
        parameter_definition_id: null,
        unit: '',
      }));
    } else {
      setShowCustomInput(false);
      setFormState((prev) => ({
        ...prev,
        parameter_definition_id: value || null,
        custom_parameter_name: '',
      }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    // Validation
    if (!showCustomInput && !formState.parameter_definition_id) {
      setFormError('Please select a parameter');
      return;
    }
    if (showCustomInput && !formState.custom_parameter_name.trim()) {
      setFormError('Please enter a parameter name');
      return;
    }
    if (!formState.value.trim()) {
      setFormError('Please enter a value');
      return;
    }
    const numValue = parseFloat(formState.value);
    if (isNaN(numValue) || numValue <= 0) {
      setFormError('Please enter a valid positive number');
      return;
    }
    if (!formState.unit.trim()) {
      setFormError('Please enter a unit');
      return;
    }
    if (!formState.test_date) {
      setFormError('Please select a test date');
      return;
    }

    onSubmit(formState);
  };

  const handleClose = () => {
    setFormState(initialFormState);
    setFormError(null);
    setShowCustomInput(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl w-full max-w-md max-h-[90vh] overflow-hidden animate-scale-in flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <DropletIcon className="w-5 h-5 text-[#E57373]" />
              <h2 className="text-lg font-semibold text-[#212121]">
                {editingReading ? 'Edit Blood Parameter' : 'Add Blood Parameter'}
              </h2>
            </div>
            <button
              onClick={handleClose}
              disabled={isSubmitting}
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <XIcon className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Parameter Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Parameter <span className="text-red-500">*</span>
            </label>
            {definitionsLoading ? (
              <div className="w-full px-4 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-500">
                Loading parameters...
              </div>
            ) : (
              <select
                value={
                  showCustomInput
                    ? 'custom'
                    : formState.parameter_definition_id || ''
                }
                onChange={handleParameterChange}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent"
                disabled={isSubmitting}
              >
                <option value="">Select a parameter...</option>
                {groupedDefinitions.map(({ category, definitions: defs }) => (
                  <optgroup key={category} label={category}>
                    {defs.map((def) => (
                      <option key={def.id} value={def.id}>
                        {def.display_name}
                      </option>
                    ))}
                  </optgroup>
                ))}
                <optgroup label="Other">
                  <option value="custom">+ Add Custom Parameter</option>
                </optgroup>
              </select>
            )}
          </div>

          {/* Custom Parameter Name (conditional) */}
          {showCustomInput && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Custom Parameter Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formState.custom_parameter_name}
                onChange={(e) =>
                  setFormState({ ...formState, custom_parameter_name: e.target.value })
                }
                placeholder="e.g., Cortisol, Insulin"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent"
                disabled={isSubmitting}
              />
            </div>
          )}

          {/* Reference Range Display (if selected parameter has one) */}
          {selectedDefinition?.reference_range && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
              <div className="text-xs text-blue-600 font-medium mb-0.5">
                Reference Range
              </div>
              <div className="text-sm text-blue-800">
                {selectedDefinition.reference_range}
              </div>
            </div>
          )}

          {/* Value and Unit */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Value <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                step="any"
                value={formState.value}
                onChange={(e) => setFormState({ ...formState, value: e.target.value })}
                placeholder="Enter value"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent"
                disabled={isSubmitting}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Unit <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formState.unit}
                onChange={(e) => setFormState({ ...formState, unit: e.target.value })}
                placeholder="e.g., mg/dL"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent"
                disabled={isSubmitting}
              />
            </div>
          </div>

          {/* Test Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Test Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={formState.test_date}
              onChange={(e) => setFormState({ ...formState, test_date: e.target.value })}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent"
              disabled={isSubmitting}
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Notes
            </label>
            <textarea
              value={formState.notes}
              onChange={(e) => setFormState({ ...formState, notes: e.target.value })}
              placeholder="Add any additional notes..."
              rows={3}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent resize-none"
              disabled={isSubmitting}
            />
          </div>

          {/* Error Message */}
          {formError && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-600">
              {formError}
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 flex gap-3">
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="flex-1 px-4 py-2.5 rounded-xl font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="flex-1 px-4 py-2.5 rounded-xl font-medium text-white bg-[#5B8E87] hover:bg-[#4A7D76] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              'Saving...'
            ) : editingReading ? (
              'Save Changes'
            ) : (
              <>
                <PlusIcon className="w-4 h-4" />
                Add Reading
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
